import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const AlertContext = createContext(null);

// ── Icon Components ────────────────────────────────────────────
const icons = {
  success: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  ),
  error: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  warning: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  ),
  info: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
    </svg>
  ),
  confirm: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
    </svg>
  ),
  delete: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
  ),
};

const typeStyles = {
  success: {
    bg: 'bg-emerald-50', border: 'border-emerald-200', iconBg: 'bg-emerald-500',
    title: 'text-emerald-900', text: 'text-emerald-700',
    toastBg: 'bg-emerald-600', toastText: 'text-white',
    progress: 'bg-emerald-400',
  },
  error: {
    bg: 'bg-red-50', border: 'border-red-200', iconBg: 'bg-red-500',
    title: 'text-red-900', text: 'text-red-700',
    toastBg: 'bg-red-600', toastText: 'text-white',
    progress: 'bg-red-400',
  },
  warning: {
    bg: 'bg-amber-50', border: 'border-amber-200', iconBg: 'bg-amber-500',
    title: 'text-amber-900', text: 'text-amber-700',
    toastBg: 'bg-amber-600', toastText: 'text-white',
    progress: 'bg-amber-400',
  },
  info: {
    bg: 'bg-blue-50', border: 'border-blue-200', iconBg: 'bg-brand-500',
    title: 'text-blue-900', text: 'text-blue-700',
    toastBg: 'bg-brand-600', toastText: 'text-white',
    progress: 'bg-brand-400',
  },
  confirm: {
    bg: 'bg-slate-50', border: 'border-slate-200', iconBg: 'bg-brand-500',
    title: 'text-slate-900', text: 'text-slate-600',
  },
  delete: {
    bg: 'bg-red-50', border: 'border-red-200', iconBg: 'bg-red-500',
    title: 'text-red-900', text: 'text-red-600',
  },
};

// ── Toast Component (bottom-right notifications) ───────────────
function Toast({ toast, onDismiss }) {
  const [exiting, setExiting] = useState(false);
  const style = typeStyles[toast.type] || typeStyles.info;
  const timerRef = useRef(null);

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(toast.id), 250);
  }, [onDismiss, toast.id]);

  useEffect(() => {
    const dur = toast.duration || 4000;
    timerRef.current = setTimeout(dismiss, dur);
    return () => clearTimeout(timerRef.current);
  }, [dismiss, toast.duration]);

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3.5 rounded-2xl shadow-lg border backdrop-blur-sm min-w-[320px] max-w-[420px] transition-all duration-250 cursor-pointer
        ${style.bg} ${style.border}
        ${exiting ? 'opacity-0 translate-x-8 scale-95' : 'opacity-100 translate-x-0 scale-100'}
      `}
      style={{ animation: exiting ? 'none' : 'toastSlideIn 0.3s cubic-bezier(0.16,1,0.3,1)' }}
      onClick={dismiss}
    >
      <div className={`w-8 h-8 rounded-xl ${style.iconBg} text-white flex items-center justify-center shrink-0 shadow-sm`}>
        {icons[toast.type] || icons.info}
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        {toast.title && <p className={`text-sm font-bold ${style.title} leading-tight`}>{toast.title}</p>}
        <p className={`text-sm ${style.text} ${toast.title ? 'mt-0.5' : ''} leading-snug`}>{toast.message}</p>
      </div>
      <button onClick={(e) => { e.stopPropagation(); dismiss(); }}
        className="shrink-0 mt-0.5 text-slate-400 hover:text-slate-600 transition-colors">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

// ── Modal Alert/Confirm Component ──────────────────────────────
function AlertModal({ alert, onClose }) {
  const [exiting, setExiting] = useState(false);
  const isConfirm = alert.isConfirmDialog || alert.type === 'confirm' || alert.type === 'delete';
  const style = typeStyles[alert.type] || typeStyles.info;

  const close = useCallback((result) => {
    setExiting(true);
    setTimeout(() => onClose(alert.id, result), 200);
  }, [onClose, alert.id]);

  // ESC to cancel
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') close(false); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [close]);

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-all duration-200
        ${exiting ? 'bg-black/0' : 'bg-black/40 backdrop-blur-[2px]'}`}
    >
      <div
        className={`w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden transition-all duration-200
          ${exiting ? 'opacity-0 scale-95 translate-y-2' : 'opacity-100 scale-100 translate-y-0'}`}
        style={{ animation: exiting ? 'none' : 'alertPopIn 0.25s cubic-bezier(0.16,1,0.3,1)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon header */}
        <div className={`flex justify-center pt-7 pb-2 ${style.bg}`}>
          <div className={`w-14 h-14 rounded-2xl ${style.iconBg} text-white flex items-center justify-center shadow-lg`}
            style={{ animation: 'alertIconBounce 0.4s cubic-bezier(0.34,1.56,0.64,1) 0.1s both' }}>
            <div className="scale-125">{icons[alert.type] || icons.info}</div>
          </div>
        </div>

        {/* Content */}
        <div className={`px-6 pt-4 pb-2 text-center ${style.bg}`}>
          {alert.title && (
            <h3 className={`text-lg font-bold ${style.title} mb-1`}>{alert.title}</h3>
          )}
          <p className={`text-sm ${style.text} leading-relaxed whitespace-pre-line`}>{alert.message}</p>
        </div>

        {/* Actions */}
        <div className={`px-5 pb-5 pt-4 ${style.bg}`}>
          {isConfirm ? (
            <div className="flex gap-2.5">
              <button
                onClick={() => close(false)}
                className="flex-1 py-2.5 text-sm font-semibold bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 transition-all active:scale-[0.98]"
              >
                Batal
              </button>
              <button
                onClick={() => close(true)}
                className={`flex-1 py-2.5 text-sm font-bold text-white rounded-xl transition-all active:scale-[0.98] shadow-sm
                  ${alert.type === 'delete'
                    ? 'bg-red-500 hover:bg-red-600'
                    : alert.type === 'warning'
                      ? 'bg-amber-500 hover:bg-amber-600'
                      : 'bg-brand-600 hover:bg-brand-700'
                  }`}
              >
                {alert.confirmText || (alert.type === 'delete' ? 'Hapus' : 'Ya, Lanjutkan')}
              </button>
            </div>
          ) : (
            <button
              onClick={() => close(true)}
              className={`w-full py-2.5 text-sm font-bold text-white rounded-xl transition-all active:scale-[0.98] shadow-sm ${style.iconBg.replace('bg-', 'bg-')} hover:opacity-90`}
            >
              {alert.confirmText || 'OK'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Provider ───────────────────────────────────────────────────
let _nextId = 1;

export function AlertProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [modals, setModals] = useState([]);
  const resolversRef = useRef({});

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const closeModal = useCallback((id, result) => {
    setModals((prev) => prev.filter((m) => m.id !== id));
    if (resolversRef.current[id]) {
      resolversRef.current[id](result);
      delete resolversRef.current[id];
    }
  }, []);

  // ── Toast (non-blocking, auto-dismiss) ──
  const toast = useCallback((message, type = 'info', opts = {}) => {
    setToasts((prev) => {
      // Deduplikasi: jangan tampilkan toast yang sama (pesan + tipe) yang masih terlihat
      const isDuplicate = prev.some((t) => t.message === message && t.type === type);
      if (isDuplicate) return prev;
      const id = _nextId++;
      return [...prev.slice(-3), { id, message, type, ...opts }];
    });
  }, []);

  const toastSuccess = useCallback((msg, opts) => toast(msg, 'success', opts), [toast]);
  const toastError = useCallback((msg, opts) => toast(msg, 'error', opts), [toast]);
  const toastWarning = useCallback((msg, opts) => toast(msg, 'warning', opts), [toast]);
  const toastInfo = useCallback((msg, opts) => toast(msg, 'info', opts), [toast]);

  // ── Modal Alert (blocking, returns promise<true>) ──
  const showAlert = useCallback((message, type = 'warning', opts = {}) => {
    return new Promise((resolve) => {
      const id = _nextId++;
      resolversRef.current[id] = resolve;
      setModals((prev) => [...prev, { id, message, type, ...opts }]);
    });
  }, []);

  // ── Confirm Dialog (blocking, returns promise<boolean>) ──
  // Usage: confirm('message') OR confirm({ message, title, confirmText })
  const confirm = useCallback((messageOrOpts, opts = {}) => {
    return new Promise((resolve) => {
      const id = _nextId++;
      resolversRef.current[id] = resolve;
      // isConfirmDialog menandai layout 2-tombol (Batal + konfirmasi), TERPISAH dari
      // `type` yang cuma mengatur styling/ikon. Jadi confirm bisa bergaya 'warning'
      // (amber) tapi tetap punya tombol Batal.
      if (typeof messageOrOpts === 'string') {
        setModals((prev) => [...prev, { id, message: messageOrOpts, type: opts.type || 'confirm', ...opts, isConfirmDialog: true }]);
      } else {
        // messageOrOpts is an object with { message, title, confirmText, ... }
        const { message, ...rest } = messageOrOpts;
        setModals((prev) => [...prev, { id, message, type: rest.type || 'confirm', ...rest, isConfirmDialog: true }]);
      }
    });
  }, []);

  // ── Delete Confirm (red variant) ──
  const confirmDelete = useCallback((message, opts = {}) => {
    return confirm(message, { type: 'delete', title: opts.title || 'Konfirmasi Hapus', confirmText: opts.confirmText || 'Hapus', ...opts });
  }, [confirm]);

  const value = {
    toast, toastSuccess, toastError, toastWarning, toastInfo,
    showAlert, confirm, confirmDelete,
  };

  return (
    <AlertContext.Provider value={value}>
      {children}

      {/* Toast Container — bottom right */}
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-[9998] flex flex-col-reverse gap-2.5 pointer-events-auto">
          {toasts.map((t) => (
            <Toast key={t.id} toast={t} onDismiss={dismissToast} />
          ))}
        </div>
      )}

      {/* Modal Alerts */}
      {modals.map((m) => (
        <AlertModal key={m.id} alert={m} onClose={closeModal} />
      ))}
    </AlertContext.Provider>
  );
}

// ── Hook ───────────────────────────────────────────────────────
export function useAlert() {
  const ctx = useContext(AlertContext);
  if (!ctx) throw new Error('useAlert must be used within AlertProvider');
  return ctx;
}

export default AlertContext;
